import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityTeamRoster } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save team data.
 */
@Injectable()
export class IdentityTeamRosterService {
    public static get INCLUDE_ATHLETES(): string {
        return 'athletes';
    }

    public static get INCLUDE_SEASON(): string {
        return 'season';
    }

    private readonly identityApiBaseUrl: string;
    private readonly identitySportName: string;
    private cachedRostersMap: { [apiRequest: string]: IdentityTeamRoster[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
        this.identitySportName = http.configService.getSportConfig('identitySportName');
    }

    public clearCache(): void {
        this.cachedRostersMap = {};
    }

    /**
     * Get all team rosters
     */
    public getIndex(teamId: string, includes: string[] = null, forceRefresh: boolean = false)
    : Observable<IdentityTeamRoster[]> {
        const apiRequestUrl: string = this.getApiUrl(teamId, null, includes, null);

        if (!forceRefresh &&
            this.cachedRostersMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedRostersMap[apiRequestUrl].map((r: IdentityTeamRoster) => r.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const teamRosters: IdentityTeamRoster[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const team of bodyData) {
                    teamRosters.push((new IdentityTeamRoster()).deserialize(team, included, this.identitySportName));
                }

                this.cachedRostersMap[apiRequestUrl] = teamRosters;
                return teamRosters.map((x: IdentityTeamRoster) => x.clone());
            }));
    }

    /**
     * Get a single team roster
     */
    public get(
        identityTeamId: string,
        identityTeamRosterId: string,
        includes: string[] = null,
    ): Observable<IdentityTeamRoster> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamRosterId, includes, null);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityTeamRoster().deserialize(
                response,
                null,
                this.identitySportName,
            ))));
    }

    /**
     * Add or update a team roster
     */
    public save(teamRoster: IdentityTeamRoster, includes: string[] = null): Observable<IdentityTeamRoster> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(teamRoster.identityTeamId, teamRoster.id, includes);

        if (teamRoster.id) {
            // Update existing Team
            response = this.http.patch(apiRequestUrl, teamRoster.serialize());
        } else {
            // Add a new Team
            response = this.http.post(apiRequestUrl, teamRoster.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeamRoster())
                    .deserialize(response, null, this.identitySportName)),
                finalize(() => this.clearCache()),
            );
    }

    public addAthletes(
        identityTeamId: string,
        identityTeamRosterId: string,
        identityPersonIds: string[],
    ): Observable<IdentityTeamRoster> {
        let apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamRosterId, null);
        apiRequestUrl += '/relationships/athletes';

        const json: any = {};
        json.data = [];

        identityPersonIds.forEach((identityPersonId: string) => {
            json.data.push({ type: 'person', id: identityPersonId });
        });

        return this.http.post(apiRequestUrl, JSON.stringify(json))
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeamRoster())
                    .deserialize(response, null, this.identitySportName)),
                finalize(() => this.clearCache()),
            );
    }

    public deleteAthletes(
        identityTeamId: string,
        identityTeamRosterId: string,
        identityPersonIds: string[],
    ): Observable<IdentityTeamRoster> {
        let apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamRosterId, null);
        apiRequestUrl += '/relationships/athletes';

        const json: any = {};
        json.data = [];
        const headers: any = { headers: this.http.getHeadersForJson(), body: JSON.stringify(json) };

        identityPersonIds.forEach((identityPersonId: string) => {
            json.data.push({ type: 'person', id: identityPersonId });
        });

        return this.http.delete(apiRequestUrl, true, true, headers)
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeamRoster())
                    .deserialize(response, null, this.identitySportName)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified roster.
     */
    public delete(identityTeamRoster: IdentityTeamRoster): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamRoster.identityTeamId, identityTeamRoster.id, null);
        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(teamId: string, teamRosterId: string, includes: string[], queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/teams/${teamId}/rosters/`;

        if (teamRosterId) {
            url += teamRosterId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

